import { defineStore, storeToRefs } from "pinia";
import { useBookingStore } from "./booking";
import {computed, watch} from "vue";
import {usePostSaleStore} from "@/stores/post-sale";

const carnetSteps = [
	'search',
	'carnet-solutions',
	'buyer-data'
];
const forwardTripSteps = [
	'forward-search',	// is useful in postSale (eg. bookingChange)
	'forward-travel-solutions',
	'forward-seat-selection' // added only when enable from user
];
const returnTripSteps = [
	'return-search',	 // is useful in postSale (eg. bookingChange)
	'return-travel-solutions',
	'return-seat-selection' // added only when enable from user
];
const finalTripSteps = [
	'travellers',
	'buyer-data',
];

export const useBookingStepperStore = defineStore('booking-stepper', {
	state: () => ({
		bookingType: 'tickets',		// tickets | carnet | postSale
		step: 'forward-search',
		stepStatus: {
			'forward-search': {
				completed: false
			},
			'forward-travel-solutions': {
				completed: false
			},
			'forward-seat-selection': {
				completed: false
			},
			'return-search': {
				completed: false
			},
			'return-travel-solutions': {
				completed: false
			},
			'return-seat-selection': {
				completed: false
			},
			'travellers': {
				completed: false
			},
			'buyer-data': {
				completed: false
			},

			'search': {
				completed: false
			},
			'carnet-solutions': {
				completed: false
			}
		},
		stepsCompleted: 0,
		requestSeatSelection: false,
		nextLoading: false,
		isRoundTrip: useBookingStore().isRoundTrip,
	}),
	getters: {
		rightSteps(state) {
			if ('postSale' == state.bookingType) {
				const postSaleStore = usePostSaleStore();
				if (postSaleStore.isForwardReturnFlow && 'change_class' == postSaleStore.selectedEdit) return ['forward-travel-solutions', 'return-travel-solutions'];
				if (postSaleStore.isForwardReturnFlow) return [...forwardTripSteps, ...returnTripSteps];

				return 'forward' == postSaleStore.travelStep ? forwardTripSteps : returnTripSteps;
			}

			if ('carnet' == state.bookingType) return carnetSteps;

			const rightSteps = useBookingStore().isRoundTrip ? [...forwardTripSteps, ...returnTripSteps, ...finalTripSteps] : [...forwardTripSteps, ...finalTripSteps];
			const deleteThis = rightSteps.indexOf('return-search');
			if (deleteThis >= 0) rightSteps.splice(deleteThis, 1);

			return rightSteps;
		},
		inSearchPage(state) {
			return this.rightSteps.filter((step) => step.includes('search')).includes(state.step);
		},
		inTravelSolutionsPage(state) {
			return this.rightSteps.filter((step) => step.includes('travel-solutions')).includes(state.step);
		},
		inSeatSelectionPage(state) {
			return this.rightSteps.filter((step) => step.includes('seat')).includes(state.step);
		},
		inPassengersDataPage(state) {
			return this.rightSteps.filter((step) => step.includes('travellers') || step.includes('buyer-data')).includes(state.step);
		},
		inForwardPages(state) {
			return this.rightSteps.filter((step) => step.includes('forward')).includes(state.step);
		},
		inReturnPages(state) {
			return this.rightSteps.filter((step) => step.includes('return')).includes(state.step);
		},
		inCarnetPages(state) {
			return carnetSteps.includes(state.step);
		},
		currentIndex(state){
			return this.rightSteps.indexOf(state.step);
		},
		nextStep(state){
			if(this.currentIndex + 1 >= this.rightSteps.length) return null;
			let nextStep = this.rightSteps[this.currentIndex + 1];

			if ('change_class' == usePostSaleStore().selectedEdit) return nextStep;

			if((state.step == 'forward-travel-solutions' && !this.seatSelectionStepEnabled) ||
				(state.step == 'return-travel-solutions' && !this.returnSeatSelectionStepEnabled)) {
				nextStep = this.rightSteps[this.currentIndex + 2];
			}

			return nextStep;
		},
		backStep(state){
			if(this.currentIndex == 0) return null;
			if(this.currentIndex + 1 == this.rightSteps.length) return this.rightSteps[0];
			let backStep = this.rightSteps[this.currentIndex - 1];

			if ('change_class' == usePostSaleStore().selectedEdit) return backStep;

			if((state.step == 'return-travel-solutions' && !this.seatSelectionStepEnabled) ||
				(state.step == 'travellers' && !this.returnSeatSelectionStepEnabled)) {
				backStep = this.rightSteps[this.currentIndex - 2];
			}

			return backStep;
		},
		seatSelectionStepEnabled(){
			return useBookingStore().forwardTravel.seats.requested;
		},
		returnSeatSelectionStepEnabled(){
			return useBookingStore().returnTravel.seats.requested;
		},
		enabledSteps(){
			const enabledSteps = [...this.rightSteps];
			enabledSteps.splice(this.currentIndex + 1);
			return enabledSteps;
		},
		stepNumbers(){
			let stepsMap = {};
			let num = 0;
			for (let i in this.rightSteps) {
				if (!Object.hasOwnProperty.call(this.rightSteps, i)) continue;
				num += 1;
				if (this.rightSteps[i].includes('forward-seat')) num = this.seatSelectionStepEnabled ? num : (num - 1);
				if (this.rightSteps[i].includes('return-seat')) num = this.returnSeatSelectionStepEnabled ? num : (num - 1);

				stepsMap[this.rightSteps[i]] = num;
			}

			return stepsMap;
		},
		currentStepNumber(state){
			return this.stepNumbers[state.step];
		},
		isAfterForwardTripSteps(state) {
			if (!usePostSaleStore().reopenedTravel && !useBookingStore().isRoundTrip) return false;

			const roundTripSteps = [...forwardTripSteps, ...returnTripSteps, ...finalTripSteps];
			const returnSteps = roundTripSteps.filter((step) => (!step.includes('forward') && !step.includes('carnet')));
			const res = returnSteps.includes(state.step);
			usePostSaleStore().travelStep = res ? 'return' : 'forward';

			return res;
		}
	},
	actions: {
		goNext(){
			this.stepStatus[this.step].completed = true;
			this.stepsCompleted++;
			this.step = this.nextStep;
		},
		goBack() {
			this.stepsCompleted--;
			this.step = this.backStep;
			this.stepStatus[this.step].completed = false;
		},
		reset(){
			this.step = 'search';
			this.nextLoading = false;
		},
		reInitialize() {
			this.$reset();
		},
		switchRoundTripStatus(value) {
			this.isRoundTrip = value;
			this.step = this.rightSteps[0];
		},
		switchBetweenTicketOrCarnet(selectedType) {
			this.bookingType = selectedType;
			this.step = 'tickets' == selectedType ? 'forward-search' : 'search';
		}
	},
	onInit(){
		// const {step} = storeToRefs(this);
		// watch(step, (newVal, oldVal) => {
		// 	// apply logic to reset o set data of booking based on step changes
		// });
	}
});